/**
 * カラー別ラインナップ页：色卡导航。
 * 1. 点击色卡平滑滚动到对应色系分组；
 * 2. 滚动时高亮当前分组对应的色卡；
 * 3. 移动端折叠/展开色卡行。
 *
 * 色卡条不做吸附：主题的 <main class="content-for-layout"> 设了 overflow: hidden，
 * 会把 position: sticky 的定位上下文变成这个永不滚动的容器，元素不再吸附到视口，
 * 而是被 top 值整体下推。这里只保留站点 header 的高度，用于锚点跳转的落点补偿。
 */
class I18PerfectMatch extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector("[data-i18-match-bar]");
    this.toggle = this.querySelector("[data-i18-match-toggle]");
    this.links = Array.from(this.querySelectorAll("[data-i18-match-link]"));
    this.groups = Array.from(this.querySelectorAll("[data-i18-match-group]"));

    this.syncMetrics();

    this.handleResize = () => {
      this.syncMetrics();
      this.syncCollapseOnResize();
      this.updateActive();
    };
    this.handleScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.ticking = false;
        this.syncMetrics();
        this.updateActive();
      });
    };

    window.addEventListener("resize", this.handleResize, { passive: true });
    window.addEventListener("scroll", this.handleScroll, { passive: true });

    this.links.forEach((link) => {
      link.addEventListener("click", (event) =>
        this.scrollToGroup(event, link),
      );
    });

    if (this.toggle) {
      this.toggle.addEventListener("click", () => this.toggleSwatches());
    }

    // header 在滚动中会切换 sticky / hidden 类，可见高度随之变化
    this.siteHeader = document.querySelector(".section-header");
    if (this.siteHeader) {
      this.headerObserver = new MutationObserver(() => this.syncMetrics());
      this.headerObserver.observe(this.siteHeader, {
        attributes: true,
        attributeFilter: ["class"],
      });
    }

    this.updateActive();
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.handleResize);
    window.removeEventListener("scroll", this.handleScroll);
    if (this.headerObserver) this.headerObserver.disconnect();
  }

  get stickyTop() {
    return (
      Number.parseFloat(
        this.style.getPropertyValue("--i18-match-sticky-top"),
      ) || 0
    );
  }

  /**
   * 站点 header 当前遮挡的高度。
   * 不用 height —— .section-header 容器可能包含公告栏、下拉菜单等不参与吸顶的部分。
   * 取它在视口中的下边缘：header 吸顶时即其可见高度；随页面滚走时为负，归零。
   */
  measureStickyTop() {
    const header = document.querySelector(".section-header");
    if (!header) return 0;
    if (header.classList.contains("shopify-section-header-hidden")) return 0;
    return Math.max(0, Math.round(header.getBoundingClientRect().bottom));
  }

  syncMetrics() {
    this.style.setProperty(
      "--i18-match-sticky-top",
      `${this.measureStickyTop()}px`,
    );
  }

  scrollToGroup(event, link) {
    const target = document.getElementById(link.dataset.i18MatchTarget);
    if (!target) return;

    event.preventDefault();
    const offset = this.stickyTop + 8;
    const top = target.getBoundingClientRect().top + window.scrollY - offset;
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    window.scrollTo({
      top: Math.max(0, top),
      behavior: prefersReducedMotion ? "auto" : "smooth",
    });
    this.setActive(link.dataset.i18MatchTarget);

    if (window.history && window.history.replaceState) {
      window.history.replaceState(null, "", `#${link.dataset.i18MatchTarget}`);
    }
  }

  updateActive() {
    if (!this.groups.length) return;

    const line = window.scrollY + this.stickyTop + 16;
    let currentId = this.groups[0].id;

    this.groups.forEach((group) => {
      if (group.getBoundingClientRect().top + window.scrollY <= line)
        currentId = group.id;
    });

    this.setActive(currentId);
  }

  setActive(id) {
    this.links.forEach((link) => {
      const isActive = link.dataset.i18MatchTarget === id;
      link.classList.toggle("is-active", isActive);
      if (isActive) {
        link.setAttribute("aria-current", "true");
      } else {
        link.removeAttribute("aria-current");
      }
    });
  }

  toggleSwatches() {
    if (!this.bar || !this.toggle) return;
    const collapsed = this.bar.classList.toggle("is-collapsed");
    this.toggle.setAttribute("aria-expanded", String(!collapsed));
  }

  // 桌面端不提供折叠能力，视口变宽后需要把状态复位
  syncCollapseOnResize() {
    if (!this.bar || !this.toggle) return;
    if (
      window.innerWidth >= 768 &&
      this.bar.classList.contains("is-collapsed")
    ) {
      this.bar.classList.remove("is-collapsed");
      this.toggle.setAttribute("aria-expanded", "true");
    }
  }
}

if (!customElements.get("i18-perfect-match")) {
  customElements.define("i18-perfect-match", I18PerfectMatch);
}
